import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { format } from 'date-fns';
import { Position } from '../types';
import { PositionService } from '../services/positionService';
import { formatCurrency } from '../utils/formatters';

interface ExecutePendingPositionDialogProps {
  open: boolean;
  onClose: () => void;
  position: Position | null;
  onExecuted: () => void;
}

export default function ExecutePendingPositionDialog({
  open,
  onClose,
  position,
  onExecuted,
}: ExecutePendingPositionDialogProps) {
  const [executionPrice, setExecutionPrice] = useState<string>('');
  const [executionDate, setExecutionDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd'T'HH:mm")
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setExecutionPrice('');
    setExecutionDate(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    setError(null);
    onClose();
  };

  const handleExecute = async () => {
    if (!position) return;

    setError(null);
    setLoading(true);

    try {
      const price = parseFloat(executionPrice);
      if (isNaN(price) || price <= 0) {
        throw new Error('Please enter a valid execution price');
      }

      const execDate = new Date(executionDate);
      if (execDate > new Date()) {
        throw new Error('Execution date cannot be in the future');
      }

      await PositionService.executePendingPosition(
        position.id,
        price,
        execDate
      );

      onExecuted();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to execute position');
    } finally {
      setLoading(false);
    }
  };

  if (!position) return null;

  const limitPrice = position.limitPrice || position.entryPrice;
  const allocatedFunds = limitPrice * position.quantity;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Execute Pending Position</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            <strong>{position.symbol}</strong> - {position.quantity} shares
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Limit Price: {formatCurrency(limitPrice)} | 
            Allocated Funds: {formatCurrency(allocatedFunds)}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Execution Price"
            type="number"
            fullWidth
            value={executionPrice}
            onChange={(e) => setExecutionPrice(e.target.value)}
            placeholder={limitPrice.toFixed(2)}
            InputProps={{
              startAdornment: '$',
              inputProps: { step: '0.01', min: '0.01' }
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Execution Date & Time"
            type="datetime-local"
            fullWidth
            value={executionDate}
            onChange={(e) => setExecutionDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="textSecondary">
              Total Cost: {formatCurrency(
                parseFloat(executionPrice || limitPrice.toString()) * position.quantity
              )}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Price Difference: {executionPrice && (
                parseFloat(executionPrice) > limitPrice ? (
                  <span style={{ color: 'red' }}>
                    +{formatCurrency(parseFloat(executionPrice) - limitPrice)} per share
                  </span>
                ) : (
                  <span style={{ color: 'green' }}>
                    -{formatCurrency(limitPrice - parseFloat(executionPrice))} per share
                  </span>
                )
              )}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExecute}
          disabled={loading || !executionPrice}
        >
          Execute Position
        </Button>
      </DialogActions>
    </Dialog>
  );
}